import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ExternalLink } from 'lucide-react';
import { api } from '@/lib/api.js';
import { toast } from '@/components/ui/toaster.jsx';
import { openDocument } from '@/lib/openDocument.js';
import { formatDateTime } from '@/lib/date-display.js';

// THE SIGNED-APPLICATION LINK REVIEW (deals-door-1 answers round 3, R2d-Q2). Each row is a deal whose Deal link names one
// WorkDrive file and whose filed copy names another. David says which one the client signed; the answer is stored against
// the exact pair shown. Round 4 (R3d-Q1): an answer naming one side corrects the other on the server (the Deal link, or the
// filed copy), and the old value is kept as a backup line under Corrected. A correction that did not land shows under
// Answered with a retry. A row leaves the list when it is answered, or when the two sides come to agree; if either side moves
// after an answer, the row comes back. The server derives everything here.

const ENDPOINT = '/api/tracon/signed-applications/link-review';
const WORKDRIVE_FILE = 'https://workdrive.zoho.com/file/';
const ANSWER_WORDS = {
  filed_is_signed: 'The filed copy is the signed one (correct the Deal link)',
  link_is_signed: 'The Deal link is the signed one (correct the filed copy)',
  both_stand: 'Both are real documents, keep both',
};

const BUTTON = 'min-h-[36px] rounded-card border border-border px-3 text-xs text-text hover:border-accent disabled:opacity-50';
const SIDE_WORDS = { deal_link: 'Deal link corrected', filed_copy: 'Filed copy corrected' };
const size = (n) => (n == null ? 'size not recorded' : `${Math.round(Number(n) / 1024)} KB`);
const when = (v) => (v ? formatDateTime(v) : 'not recorded');

function OpenLink({ href, children }) {
  if (!href) return <span className="text-text-dim">{children}</span>;
  return (
    <button type="button" onClick={() => openDocument(href)} className="inline-flex min-h-[36px] items-center gap-1 text-info hover:text-accent">
      {children} <ExternalLink size={11} strokeWidth={1.5} />
    </button>
  );
}

function FiledLine({ f }) {
  const href = f.permalink || (f.file_id ? `${WORKDRIVE_FILE}${f.file_id}` : null);
  const from = f.source === 'bind' ? 'the bind\'s filed PDF' : `filed row (${f.match_method || 'no method'}${f.from_outside ? ', from outside' : ''})`;
  return (
    <li className="flex flex-wrap items-center gap-x-3 text-xs text-text-muted">
      <OpenLink href={href}>Filed copy</OpenLink>
      <span>{from}</span>
      <span className="font-mono">file {f.file_id}</span>
      <span>received {when(f.received_at)}</span>
      {f.source === 'bind' ? <span>signed {when(f.signed_at)}</span> : null}
      <span>{size(f.file_size)}</span>
    </li>
  );
}

function ReviewRow({ row, onAnswered }) {
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const answer = async (a, signedFileId = null) => {
    setBusy(true);
    try {
      const r = await api.post(`${ENDPOINT}/${encodeURIComponent(row.crm_deal_id)}`, { answer: a, note, ...(signedFileId ? { signed_file_id: signedFileId } : {}) });
      toast.success(`Recorded: ${ANSWER_WORDS[a]}.`);
      if (r?.correction && !r.correction.done) toast.error(`Not corrected: ${r.correction.error || 'the correction did not land'}. Your answer is kept; answer again to retry.`);
      else if (r?.correction?.done) toast.info(r.correction.side === 'deal_link' ? 'The Deal link now names the filed copy; the old link is kept as a backup line.' : 'The filed copy now names the Deal link\'s file; the old file is kept as a backup line.');
      await onAnswered();
    } catch (e) {
      toast.error(e.body?.error || e.message || 'Could not record the answer');
      await onAnswered();
    } finally {
      setBusy(false);
    }
  };
  return (
    <li className="space-y-2 py-3" data-link-review={row.crm_deal_id}>
      <div className="flex flex-wrap items-baseline gap-x-3 text-sm">
        <Link to={`/account/${encodeURIComponent(row.crm_deal_id)}/deal/${encodeURIComponent(row.crm_deal_id)}`} className="font-medium text-text hover:text-accent">Deal {row.crm_deal_id}</Link>
        <span className="text-xs text-text-muted">{row.app_number || 'no application number'}</span>
        {row.stale_answers ? <span className="text-xs text-warning">{row.stale_answers} earlier answer(s) judged a pair that has since moved</span> : null}
      </div>
      <div className="flex flex-wrap items-center gap-x-3 text-xs text-text-muted">
        <OpenLink href={row.link.url}>Deal link</OpenLink>
        <span className="font-mono">file {row.link.file_id || 'none named'}</span>
        <span>size not read (WorkDrive is not called here)</span>
      </div>
      <ul className="space-y-1">{row.filed.map((f) => <FiledLine key={`${f.source}:${f.file_id}`} f={f} />)}</ul>
      <label className="block text-xs text-text-muted">
        Note
        <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2} className="mt-1 w-full rounded-card border border-border bg-bg p-2 text-sm text-text" />
      </label>
      <div className="flex flex-wrap gap-2">
        {row.filed.length > 1
          ? row.filed.map((f) => (
            <button key={`pick:${f.file_id}`} type="button" disabled={busy} onClick={() => answer('filed_is_signed', f.file_id)} className={BUTTON}>Filed file {f.file_id} is the signed one (correct the Deal link)</button>
          ))
          : <button type="button" disabled={busy} onClick={() => answer('filed_is_signed', row.filed[0]?.file_id)} className={BUTTON}>{ANSWER_WORDS.filed_is_signed}</button>}
        {['link_is_signed', 'both_stand'].map((a) => (
          <button key={a} type="button" disabled={busy} onClick={() => answer(a)} className={BUTTON}>{ANSWER_WORDS[a]}</button>
        ))}
      </div>
    </li>
  );
}

function AnsweredRow({ row, onAnswered }) {
  const [busy, setBusy] = useState(false);
  const retry = async () => {
    setBusy(true);
    try {
      const r = await api.post(`${ENDPOINT}/${encodeURIComponent(row.crm_deal_id)}`, { answer: row.review.answer, ...(row.review.correction?.to?.file_id && row.review.answer === 'filed_is_signed' ? { signed_file_id: row.review.correction.to.file_id } : {}) });
      if (r?.correction?.done) toast.success('Corrected; the old value is kept as a backup line.');
      else toast.error(`Not corrected: ${r?.correction?.error || 'the correction did not land'}`);
    } catch (e) {
      toast.error(e.body?.error || e.message || 'Could not correct');
    } finally {
      setBusy(false);
      await onAnswered();
    }
  };
  return (
    <li className="py-2 text-xs text-text-muted">
      <span className="font-medium text-text">Deal {row.crm_deal_id}</span>
      {' · '}{ANSWER_WORDS[row.review.answer] || row.review.answer}
      {' · '}{row.review.decided_by} {when(row.review.decided_at)}
      {row.correction_pending ? (
        <span className="mt-1 flex flex-wrap items-center gap-2">
          <span className="text-warning">Not corrected yet</span>
          <button type="button" disabled={busy} onClick={retry} className={BUTTON}>Correct now</button>
        </span>
      ) : null}
      {row.review.note ? <span className="block text-text-dim">{row.review.note}</span> : null}
    </li>
  );
}

export default function SignedApplicationReview() {
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const refresh = useCallback(async () => {
    try {
      setData(await api.get(ENDPOINT, { timeoutMs: 60_000 }));
      setError('');
    } catch (e) {
      setError(e.body?.error || e.message || 'Could not load the review list');
    }
  }, []);
  useEffect(() => { refresh(); }, [refresh]);

  return (
    <div className="p-4">
      <h2 className="text-xl font-semibold text-text">Signed applications to review</h2>
      <p className="mt-1 text-sm text-text-muted">
        The Deal link names one WorkDrive file and the filed copy another. Say which one the client signed: the other is corrected to match, and the old one is kept as a backup line under Corrected. Your answer is kept against the two files shown.
      </p>
      {error ? <p className="mt-4 text-sm text-danger">{error}</p> : null}
      {data === null && !error ? <p className="mt-4 text-sm text-text-muted">Loading&hellip;</p> : null}
      {data ? (
        <>
          <p className="mt-4 font-mono text-sm text-text">
            {data.counts.open} to review · {data.counts.answered} answered · {data.counts.with_link} deals carry a link
          </p>
          {data.counts.permalink_repointed ? (
            <p className="mt-1 text-xs text-text-muted">{data.counts.permalink_repointed} more are the half-moved regen, not real differences: the file-id repair (runbook step 9a) clears them.</p>
          ) : null}
          {data.open.length === 0 ? <p className="mt-4 text-sm text-text-muted">Nothing to review.</p> : (
            <ul className="mt-3 divide-y divide-border rounded-card border border-border bg-surface px-3">
              {data.open.map((row) => <ReviewRow key={row.crm_deal_id} row={row} onAnswered={refresh} />)}
            </ul>
          )}
          {data.answered.length ? (
            <details className="mt-6">
              <summary className="cursor-pointer text-sm text-text-muted">Answered ({data.answered.length})</summary>
              <ul className="mt-2 divide-y divide-border rounded-card border border-border bg-surface px-3">
                {data.answered.map((row) => <AnsweredRow key={row.crm_deal_id} row={row} onAnswered={refresh} />)}
              </ul>
            </details>
          ) : null}
          {data.corrected?.length ? (
            <details className="mt-6">
              <summary className="cursor-pointer text-sm text-text-muted">Corrected ({data.corrected.length})</summary>
              <ul className="mt-2 divide-y divide-border rounded-card border border-border bg-surface px-3">
                {data.corrected.map((c) => (
                  <li key={c.crm_deal_id} className="space-y-1 py-2 text-xs text-text-muted" data-link-corrected={c.crm_deal_id}>
                    <div>
                      <span className="font-medium text-text">Deal {c.crm_deal_id}</span>
                      {' · '}{SIDE_WORDS[c.side] || c.side}
                      {' · '}{c.review.decided_by} {when(c.review.decided_at)}
                    </div>
                    <div className="flex flex-wrap items-center gap-x-3"><OpenLink href={c.to.url}>Now</OpenLink><span className="font-mono">file {c.to.file_id}</span></div>
                    <div className="flex flex-wrap items-center gap-x-3 text-text-dim">
                      <span>Backup:</span>
                      <OpenLink href={c.backup.url || (c.backup.file_id ? `${WORKDRIVE_FILE}${c.backup.file_id}` : null)}>{c.side === 'deal_link' ? 'the old Deal link' : (c.backup.transaction_id ? 'the bind\'s filed PDF (kept on the bind)' : 'the old filed copy')}</OpenLink>
                      <span className="font-mono">file {c.backup.file_id || 'none'}</span>
                    </div>
                    {c.review.note ? <span className="block text-text-dim">{c.review.note}</span> : null}
                  </li>
                ))}
              </ul>
            </details>
          ) : null}
        </>
      ) : null}
    </div>
  );
}
